import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Loader2, MailCheck } from "lucide-react";
import { AREAS } from "@/data/areas";

const API = process.env.REACT_APP_BACKEND_URL;
const AREA_NAME = Object.fromEntries(AREAS.map((a) => [a.id, a.name]));

const EMPTY = {
  business_name: "",
  website: "",
  description: "",
  area: "",
  category: "",
  contact_name: "",
  email: "",
  phone: "",
  address: "",
};

export const AdPurchase = () => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/api/ads/packages`).then((r) => setData(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/api/ads/inquiry`, { ...form, package: "", addon_website: false });
      setSubmitted(true);
      setLoading(false);
      setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Something went wrong — please check your details and try again.");
      setLoading(false);
    }
  };

  if (!data) return null;

  return (
    <section id="packages" data-testid="ad-packages" className="bg-[#F9F8F6] border-y border-dotted border-zinc-300">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24">
        <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226] mb-4">GET IN TOUCH</p>
        <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl max-w-3xl">
          Advertise with us,<br />
          <span className="font-editorial italic font-600">reach every visitor</span>
        </h2>
        <p className="font-body text-base md:text-lg text-black/70 max-w-2xl mt-6">
          Tell us about your business and we&apos;ll get you set up personally — a listing on the guide
          and a pin on the map, tailored to your spot. We&apos;ll be in touch about availability and pricing.
        </p>
        <p data-testid="ad-scarcity" className="inline-flex items-center gap-2 mt-5 font-body font-800 text-sm text-[#DF3226] bg-[#DF3226]/10 border border-[#DF3226]/20 rounded-full px-4 py-2">
          Spots are limited — we keep it to a handful of partners per area.
        </p>

        <div ref={formRef} data-testid="ad-form" className="mt-10 bg-white border border-zinc-200 shadow-brut-lg p-6 md:p-9 scroll-mt-24">
          {submitted ? (
            <div data-testid="ad-inquiry-success" className="text-center py-6">
              <span className="w-14 h-14 grid place-items-center bg-[#DF3226]/10 text-[#DF3226] rounded-2xl mx-auto mb-4">
                <MailCheck size={26} />
              </span>
              <h3 className="font-display font-900 tracking-tight text-2xl md:text-3xl">Enquiry received — thank you!</h3>
              <p className="font-body text-base text-black/60 mt-3 max-w-md mx-auto">
                We&apos;ve got your details. We&apos;ll email you shortly about availability and getting your listing live.
              </p>
            </div>
          ) : (
            <>
              <h3 className="font-display font-900 tracking-tight text-2xl md:text-3xl">Your details</h3>
              <p className="font-body text-sm text-black/60 mt-2">
                Send us your details and we&apos;ll be in touch to get you live. The address is used to drop your map pin.
              </p>
              <form onSubmit={submit} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Business name" testid="ad-f-business" value={form.business_name} onChange={set("business_name")} required />
                <Field label="Website URL (optional)" testid="ad-f-website" value={form.website} onChange={set("website")} placeholder="https://" />
                <div className="md:col-span-2">
                  <FieldLabel>Short description</FieldLabel>
                  <textarea
                    data-testid="ad-f-description"
                    value={form.description}
                    onChange={set("description")}
                    required
                    maxLength={400}
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border border-zinc-300 font-body text-base focus:outline-none focus:border-[#DF3226]"
                    placeholder="One or two lines travellers will read on your listing."
                  />
                </div>
                <div>
                  <FieldLabel>Area / neighbourhood</FieldLabel>
                  <select data-testid="ad-f-area" value={form.area} onChange={set("area")} required className="w-full px-4 py-3 rounded-xl border border-zinc-300 font-body text-base bg-white focus:outline-none focus:border-[#DF3226]">
                    <option value="">Select an area…</option>
                    {data.areas.map((id) => (
                      <option key={id} value={id}>{AREA_NAME[id] || id}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <FieldLabel>Category</FieldLabel>
                  <select data-testid="ad-f-category" value={form.category} onChange={set("category")} required className="w-full px-4 py-3 rounded-xl border border-zinc-300 font-body text-base bg-white focus:outline-none focus:border-[#DF3226]">
                    <option value="">Select a category…</option>
                    {data.categories.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <FieldLabel>Business address (for the map pin)</FieldLabel>
                  <input data-testid="ad-f-address" value={form.address} onChange={set("address")} required className="w-full px-4 py-3 rounded-xl border border-zinc-300 font-body text-base focus:outline-none focus:border-[#DF3226]" placeholder="Street & number, Copenhagen" />
                </div>
                <Field label="Contact name" testid="ad-f-contact" value={form.contact_name} onChange={set("contact_name")} required />
                <Field label="Email" testid="ad-f-email" type="email" value={form.email} onChange={set("email")} required />
                <Field label="Phone" testid="ad-f-phone" value={form.phone} onChange={set("phone")} required />

                {error && (
                  <p data-testid="ad-form-error" className="md:col-span-2 font-body text-sm text-[#DF3226]">{error}</p>
                )}

                <div className="md:col-span-2 flex items-center gap-4 pt-2">
                  <button
                    type="submit"
                    data-testid="ad-submit"
                    disabled={loading}
                    className="inline-flex items-center gap-2 font-display font-900 text-lg bg-[#DF3226] text-white px-8 py-4 rounded-full shadow-brut hover:-translate-y-0.5 transition-all disabled:opacity-60"
                  >
                    {loading && <Loader2 className="animate-spin" size={18} />}
                    Send enquiry
                  </button>
                  <span className="font-body text-xs text-black/40">
                    No payment now — we&apos;ll confirm availability and pricing personally.
                  </span>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

const FieldLabel = ({ children }) => (
  <label className="font-body font-700 text-xs uppercase tracking-wide text-black/50 mb-1 block">{children}</label>
);

const Field = ({ label, testid, value, onChange, type = "text", required, placeholder }) => (
  <div>
    <FieldLabel>{label}</FieldLabel>
    <input
      data-testid={testid}
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      className="w-full px-4 py-3 rounded-xl border border-zinc-300 font-body text-base focus:outline-none focus:border-[#DF3226]"
    />
  </div>
);
